import { useTranslation } from "react-i18next"

import type { HostedAgentHealth } from "@/api/admin-api"

import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import {
  formatHermesVersionLabel,
  formatHostedAgentSecondsAgo,
  HOSTED_AGENT_HEALTH_STATE_DEFAULTS,
  hostedAgentHealthVariant,
} from "../utils/hosted-agent-health"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

/**
 * Health chip for one hosted agent (#3039): the computed state as a coloured
 * badge, "last seen 3 minutes ago · v0.0.110" as secondary text, and the
 * failure counts / last outcomes in a tooltip. Shared by the table row and
 * the map so every surface reads the same words for the same state.
 */
export function HostedAgentHealthBadge({
  health,
  showDetail = true,
}: {
  health: HostedAgentHealth
  /** Render the "last seen" line under the badge (off for compact surfaces). */
  showDetail?: boolean
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const stateLabel = t(`adminDashboard.hostedAgents.health.states.${health.state}`, {
    defaultValue: HOSTED_AGENT_HEALTH_STATE_DEFAULTS[health.state],
  })
  const ago = formatHostedAgentSecondsAgo(health.seconds_since_contact, locale)
  const version = formatHermesVersionLabel(health.last_version)
  let seenLine: string
  if (!ago) {
    seenLine = t("adminDashboard.hostedAgents.health.neverSeen", { defaultValue: "Never seen" })
  } else if (version) {
    seenLine = t("adminDashboard.hostedAgents.health.lastSeenAgoVersion", {
      defaultValue: "Last seen {{ago}} · {{version}}",
      ago,
      version,
    })
  } else {
    seenLine = t("adminDashboard.hostedAgents.health.lastSeenAgo", {
      defaultValue: "Last seen {{ago}}",
      ago,
    })
  }
  const failuresLine = t("adminDashboard.hostedAgents.health.failures", {
    defaultValue: "Failed runs: {{hour}} in the last hour · {{day}} in the last 24 h",
    hour: health.failed_runs_1h,
    day: health.failed_runs_24h,
  })
  const lastCompleted = formatHostedAgentTimestamp(health.last_completed_at, locale)
  const lastFailed = formatHostedAgentTimestamp(health.last_failed_at, locale)

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex max-w-full min-w-0 cursor-help flex-col items-start gap-0.5"
            data-state-health={health.state}
            data-testid="hosted-agent-health"
          >
            <Badge
              data-testid="hosted-agent-health-badge"
              variant={hostedAgentHealthVariant(health.state)}
            >
              {stateLabel}
            </Badge>
            {showDetail ? (
              <span
                className="text-muted-foreground max-w-full truncate text-xs"
                data-testid="hosted-agent-health-detail"
                title={seenLine}
              >
                {seenLine}
              </span>
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{seenLine}</p>
          <p>{failuresLine}</p>
          {lastCompleted ? (
            <p>
              {t("adminDashboard.hostedAgents.health.lastCompleted", {
                defaultValue: "Last completed: {{at}}",
                at: lastCompleted,
              })}
            </p>
          ) : null}
          {lastFailed ? (
            <p>
              {t("adminDashboard.hostedAgents.health.lastFailed", {
                defaultValue: "Last failed: {{at}}",
                at: lastFailed,
              })}
            </p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
